import React from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';

// Import our extracted components
import BasicInfoSection from './BasicInfoSection';
import LocationSection from './LocationSection';
import MediaUploadSection from './MediaUploadSection';
import ScheduleSection from './ScheduleSection';
import SongsSection from './SongsSection';

// Import our custom hooks
import { useDisplayForm, DisplaySchedule, DisplaySong } from './hooks/useDisplayForm';
import { useMediaUpload } from './hooks/useMediaUpload';

const EditDisplayForm: React.FC = () => {
  const { id } = useParams();
  const {
    form,
    setForm,
    isLoading,
    latitude,
    longitude,
    setLatitude,
    setLongitude,
    handleSubmit
  } = useDisplayForm(id);

  const { uploadingImages, uploadImages, deleteImage } = useMediaUpload();

  // Handler functions for form sections
  const handleBasicInfoChange = (field: string, value: string | string[]) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleLocationChange = (location: string, coordinates?: { lat: number; lng: number }) => {
    setForm(prev => ({ ...prev, location }));
    if (coordinates) {
      setLatitude(coordinates.lat);
      setLongitude(coordinates.lng);
    }
  };

  const handleImagesChange = (images: string[]) => {
    setForm(prev => ({ ...prev, images }));
  };

  const handleUploadImages = async (files: FileList) => {
    try {
      const uploadedUrls = await uploadImages(files);
      const newImages = [...form.images, ...uploadedUrls];
      handleImagesChange(newImages);
    } catch (error) {
      // Error is already handled in the hook
    }
  };

  const handleScheduleChange = (schedule: DisplaySchedule) => {
    setForm(prev => ({ ...prev, schedule }));
  };

  const handleSongsChange = (songs: DisplaySong[]) => {
    setForm(prev => ({ ...prev, songs }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <NavBar />
        <div className="container max-w-4xl py-8">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="ml-2">Loading display...</span>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container max-w-4xl py-8">
        <h1 className="text-3xl font-bold mb-8">
          {id ? 'Edit Display' : 'Add Your Display'}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information Section */}
          <BasicInfoSection
            name={form.name}
            description={form.description}
            displayType={form.display_type}
            holidays={form.holidays}
            onNameChange={(value) => handleBasicInfoChange('name', value)}
            onDescriptionChange={(value) => handleBasicInfoChange('description', value)}
            onDisplayTypeChange={(value) => handleBasicInfoChange('display_type', value)}
            onHolidaysChange={(value) => handleBasicInfoChange('holidays', value)}
          />

          {/* Location Section */}
          <LocationSection
            location={form.location}
            latitude={latitude}
            longitude={longitude}
            onLocationChange={handleLocationChange}
          />

          {/* Media Upload Section */}
          <MediaUploadSection
            images={form.images}
            uploadingImages={uploadingImages}
            onImagesChange={handleImagesChange}
            onUploadImages={handleUploadImages}
          />

          {/* Schedule Section */}
          <ScheduleSection
            schedule={form.schedule}
            onScheduleChange={handleScheduleChange}
          />

          {/* Songs Section */}
          <SongsSection
            songs={form.songs}
            onSongsChange={handleSongsChange}
          />

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <Button type="button" variant="outline" onClick={() => window.history.back()}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                id ? 'Update Display' : 'Create Display'
              )}
            </Button>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
};

export default EditDisplayForm;